import { Button } from '@unified-lk/ui'
import { formatDateTime } from '../../lib/format'
import type { StaffPatientActivityData } from '../../types/plans'
import {
  workspaceActionsClassName,
  workspaceEmptyClassName,
  workspaceFieldClassName,
  workspaceFieldLabelClassName,
  workspaceGridClassName,
  workspaceHeaderClassName,
  workspaceInputClassName,
  workspaceKickerClassName,
  workspaceMetricClassName,
  workspaceMutedTextClassName,
  workspacePanelClassName,
  workspaceSummaryGridClassName,
  workspaceTableCardRowClassName,
  workspaceTableClassName,
} from '../workspaceStyles'

const eventLabels: Record<string, string> = {
  'patient.auth.login': 'Вход по коду',
  'patient.auth.secret_login': 'Secret-вход',
  'patient.auth.dev_login': 'Dev-вход',
  'patient.auth.session_resume': 'Возврат по сессии',
  'patient.auth.logout': 'Выход',
  'patient.auth.request_code_failed': 'Ошибка запроса кода',
  'patient.auth.verify_failed': 'Ошибка проверки кода',
  'patient.navigation.view': 'Переход',
  'patient.business.booking.created': 'Онлайн-запись',
  'patient.business.review.submitted': 'Отзыв',
  'patient.business.useful.submitted': 'Заявка «Полезное»',
  'patient.business.message.read': 'Сообщение прочитано',
  'patient.business.message.open': 'Сообщение открыто',
  'patient.business.message.pin_click': 'Закрепление сообщения',
  'patient.business.message.reply_sent': 'Ответ на сообщение',
  'patient.error.data_layer_duplicate': 'Дубль в data layer',
}

function isVerificationScriptEvent(payload: Record<string, unknown> | undefined): boolean {
  return payload?.source === 'verification_script'
}

export function PatientActivitySection({
  data,
  loading,
  error,
  searchValue,
  daysValue,
  eventTypeValue,
  hideVerificationScript,
  onSearchChange,
  onDaysChange,
  onEventTypeChange,
  onHideVerificationScriptChange,
  onFilterSubmit,
  onRetry,
}: {
  data: StaffPatientActivityData | null
  loading: boolean
  error: string | null
  searchValue: string
  daysValue: string
  eventTypeValue: string
  hideVerificationScript: boolean
  onSearchChange: (value: string) => void
  onDaysChange: (value: string) => void
  onEventTypeChange: (value: string) => void
  onHideVerificationScriptChange: (value: boolean) => void
  onFilterSubmit: () => void
  onRetry: () => void
}) {
  if (loading && !data) {
    return <div className={workspaceEmptyClassName}><p>Загружаем активность пациентов...</p></div>
  }

  if (!data) {
    return <div className={workspaceEmptyClassName}><p>{error ?? 'Раздел активности пока недоступен.'}</p></div>
  }

  const errors = data.errors ?? []
  const phonesHidden = [...data.events, ...errors].some((event) => event.phone_hidden)
  const authFailures = data.summary.auth_failures ?? 0
  const business = data.summary.business ?? 0

  return (
    <div className={workspaceGridClassName}>
      <section className={workspacePanelClassName}>
        <div className={workspaceHeaderClassName}>
          <div className="grid gap-2">
            <p className={workspaceKickerClassName}>Patient Activity</p>
            <h2 className="text-2xl font-semibold text-slate-900">Активность ЛК</h2>
            <p className={workspaceMutedTextClassName}>Бизнес-лог входов пациента, ошибок OTP и ключевых действий в личном кабинете. Сбои OTP показываются только в блоке «Ошибки».</p>
            {data.sources ? <p className="text-xs leading-5 text-slate-500">Source: `{data.sources.activity}`</p> : null}
          </div>
          <div className={workspaceActionsClassName}>
            <Button variant="secondary" type="button" onClick={onRetry}>Обновить</Button>
          </div>
        </div>

        <div className={workspaceSummaryGridClassName}>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Всего событий</span><strong className="text-slate-900">{data.summary.total}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Входы</span><strong className="text-slate-900">{data.summary.logins}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Сбои OTP</span><strong className={authFailures > 0 ? 'text-rose-700' : 'text-slate-900'}>{authFailures}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Переходы</span><strong className="text-slate-900">{data.summary.navigations}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Бизнес</span><strong className="text-slate-900">{business}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Ошибки</span><strong className={data.summary.errors > 0 ? 'text-rose-700' : 'text-slate-900'}>{data.summary.errors}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Пациентов</span><strong className="text-slate-900">{data.summary.unique_patients}</strong></div>
        </div>

        <div className="grid gap-3 lg:grid-cols-[120px_minmax(0,1fr)_220px_auto] lg:items-end">
          <div className={workspaceFieldClassName}>
            <span className={workspaceFieldLabelClassName}>Период, дней</span>
            <input className={workspaceInputClassName} value={daysValue} onChange={(event) => onDaysChange(event.target.value)} inputMode="numeric" />
          </div>
          <div className={workspaceFieldClassName}>
            <span className={workspaceFieldLabelClassName}>Поиск</span>
            <input className={workspaceInputClassName} value={searchValue} onChange={(event) => onSearchChange(event.target.value)} placeholder="ФИО, телефон, ID, раздел..." />
          </div>
          <div className={workspaceFieldClassName}>
            <span className={workspaceFieldLabelClassName}>Тип события</span>
            <select className={workspaceInputClassName} value={eventTypeValue} onChange={(event) => onEventTypeChange(event.target.value)}>
              <option value="">Все события</option>
              <option value="patient.auth.login">Вход по коду</option>
              <option value="patient.auth.secret_login">Secret-вход</option>
              <option value="patient.auth.session_resume">Возврат по сессии</option>
              <option value="patient.auth.logout">Выход</option>
              <option value="patient.auth.request_code_failed">Сбой запроса кода</option>
              <option value="patient.auth.verify_failed">Сбой проверки кода</option>
              <option value="patient.navigation.view">Переходы</option>
              <option value="patient.business.booking.created">Онлайн-запись</option>
              <option value="patient.business.review.submitted">Отзывы</option>
              <option value="patient.business.useful.submitted">Заявки «Полезное»</option>
              <option value="patient.error.data_layer_duplicate">Ошибки data layer</option>
            </select>
          </div>
          <Button type="button" onClick={onFilterSubmit}>Применить</Button>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={hideVerificationScript}
            onChange={(event) => onHideVerificationScriptChange(event.target.checked)}
          />
          Скрыть автотесты (verification_script)
        </label>

        {phonesHidden ? (
          <div className="rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
            Телефоны скрыты: у пользователя нет права `patients_phone`.
          </div>
        ) : null}
      </section>

      {error ? <div className="rounded-[20px] bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700"><p>{error}</p></div> : null}

      <section className={workspacePanelClassName}>
        <div className={workspaceHeaderClassName}>
          <div className="grid gap-2">
            <p className={workspaceKickerClassName}>Patient Errors</p>
            <h3 className="text-xl font-semibold text-slate-900">Ошибки и аномалии</h3>
            <p className={workspaceMutedTextClassName}>Сбои OTP, структурные ошибки и другие события, которые могли повлиять на вход или работу ЛК.</p>
          </div>
        </div>

        {errors.length === 0 ? (
          <div className={workspaceEmptyClassName}>За выбранный период ошибок не найдено.</div>
        ) : (
          <div className="grid gap-3">
            {errors.map((event) => (
              <article key={`error-${event.id}`} className="grid gap-2 rounded-[20px] border border-rose-100 bg-rose-50 px-4 py-4 text-sm leading-6 text-rose-900">
                <div className="flex flex-col gap-1 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <strong className="block">{event.title ?? eventLabels[event.event_type] ?? event.event_type}</strong>
                    <span className="text-rose-700">{event.description ?? 'Без описания'}</span>
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-[0.08em] text-rose-700">{formatDateTime(event.created_at)}</span>
                </div>
                <div className="grid gap-1 text-xs text-rose-700">
                  <span>Пациент: {event.patient_name ?? `#${event.patient_id ?? 'n/a'}`}{event.phone ? ` · ${event.phone}` : ''}</span>
                  <span>Тип: {eventLabels[event.event_type] ?? event.event_type}</span>
                  {typeof event.payload?.error === 'string' ? <span>Код: {event.payload.error}</span> : null}
                  {isVerificationScriptEvent(event.payload) ? <span>Источник: автотест</span> : null}
                  {typeof event.payload?.items_count === 'number' ? <span>Найдено строк: {event.payload.items_count}</span> : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className={workspacePanelClassName}>
        {data.events.length === 0 ? (
          <div className={workspaceEmptyClassName}>За выбранный период событий не найдено.</div>
        ) : (
          <div className={workspaceTableClassName}>
            <div className="hidden grid-cols-[190px_minmax(0,1fr)_160px_minmax(0,1fr)] gap-4 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 lg:grid">
              <span>Время</span>
              <span>Пациент</span>
              <span>Событие</span>
              <span>Детали</span>
            </div>
            {data.events.map((event) => (
              <div key={event.id} className={`${workspaceTableCardRowClassName} lg:grid-cols-[190px_minmax(0,1fr)_160px_minmax(0,1fr)]`}>
                <span>{formatDateTime(event.created_at)}</span>
                <span>
                  <strong className="block text-slate-900">{event.patient_name ?? `Пациент #${event.patient_id ?? 'n/a'}`}</strong>
                  <small className="text-slate-500">
                    ID: {event.patient_id ?? 'n/a'}{event.phone ? ` · ${event.phone}` : event.phone_hidden ? ' · телефон скрыт' : ''}
                  </small>
                </span>
                <span>
                  <strong className="block text-slate-900">{eventLabels[event.event_type] ?? event.event_type}</strong>
                  <small className="text-slate-500">{event.event_type}</small>
                </span>
                <span>
                  <strong className="block text-slate-900">{event.title ?? event.view_label ?? 'Событие'}</strong>
                  <small className="break-all text-slate-500">{event.description || event.path || event.view || 'Без деталей'}</small>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
